import React from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { Tracking, TrackingProvider } from '../utils'
import { CrmApiContextProvider, createCrmApiClient } from './CrmApi'
import { OrderApiContextProvider, createOrderApiClient } from './OrderApi'
import { SchNetApiContextProvider, createSchNetApiClient } from './SchNetApi'

export const jsonHeaders = {
  'Accept': 'application/json',
  'Content-Type': 'application/json'
}

export function camelToSnakeCase(camelCase) {
  return camelCase.replace(/[A-Z]/g, (match) => '_' + match.toLowerCase())
}

export const prepareApiRequest = ({ apiUrl, apiToken, endpoint }) => {
  return {
    url: `${apiUrl}${endpoint}`,
    headers: {
      'Authorization': `Bearer ${apiToken}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    mode: 'cors'
  }
}

export const withResponseHandling = async (res) => {
  let json
  try {
    json = await res.json()
  } catch(e) {
    return Promise.reject(new Error(`Content ist kein JSON: ${e.message}`))
  }

  if (res.ok) {
    return json
  } else {
    throw new Error(`Api Error: ${JSON.stringify(json)}`)
  }
}

// Name all component functions here for better debugging (shown in stacktraces)

// NOTE: this variant wraps the given component with a query client provider (e.g. for components that expect that they are within a react-query contexT)
export const withQueryClient = Component => function WithQueryClient({ queryClient, ...props }) {
  return (<QueryClientProvider client={queryClient}><Component {...props} /></QueryClientProvider>)
}

export const withCrmApiClient = Component => function WithCrmApiClient({ crmApiUrl, crmApiToken, ...props }) {
  const client = createCrmApiClient({ apiUrl: crmApiUrl, apiToken: crmApiToken })
  return (
    <CrmApiContextProvider client={client}>
      <Component {...props} {...{ crmApiUrl, crmApiToken }} />
    </CrmApiContextProvider>
  )
}

export const withTracker = Component => function WithTrackingProvider({ gaId, mtId, mtUrl, ...props }) {
  const tracker = new Tracking({ enabled: !!gaId || !!mtId, gaId, mtId, mtUrl })
  return (<TrackingProvider tracker={tracker}><Component {...props} /></TrackingProvider>)
}

export const withOrderApiClient = Component => function WithOrderApiClient({ orderApiUrl, orderApiToken, ...props }) {
  const client = createOrderApiClient({ apiUrl: orderApiUrl, apiToken: orderApiToken })
  return (
    <OrderApiContextProvider client={client}>
      <Component {...props} {...{ orderApiUrl, orderApiToken }}/>
    </OrderApiContextProvider>
  )
}

export const withSchNetApiClient = Component => function WithSchNetApiClient({ programApiUrl, ...props }) {
  const client = createSchNetApiClient({ apiUrl: programApiUrl })
  return (<SchNetApiContextProvider client={client}><Component {...props} /></SchNetApiContextProvider>)
}

export const isOrganisationPrioritized = ({ organisation, filterCountryCode, filterContext }) => {
  // TODO filter by start/end date
  const activePriorityPlacements = organisation.priority_placements

  if (activePriorityPlacements.length === 0) {
    return false
  }

  let potentialPlacements = activePriorityPlacements.filter(({ country_code }) => {
    if (filterCountryCode) {
      return country_code === filterCountryCode
    } else {
      // api responds with empty string when no countryCode is explicitly set
      // but `undefined != ""` and also `null != ""`
      return country_code === ""
    }
  })

  if (potentialPlacements.length === 0) {
    return false
  }

  potentialPlacements = potentialPlacements.filter(({ context }) => {
    if (filterContext) {
      return context === filterContext
    } else {
      // api responds with empty string when no context is explicitly set
      // but `undefined != ""` and also `null != ""`
      return context === ""
    }
  })

  return potentialPlacements.length > 0
}
